<template>
  <div>
    <photo-list
      v-if="value.length"
      v-model="photosByConcept"
      :use-file-icons="useFileIcons"
      :show-labels="showLabels"
      :image-actions="!noImageActions"
      :isExtranet="isExtranet"
      :missingConcepts="missingConcepts"
    >
      <template slot="default">
        <slot name="default"></slot>
      </template>
      <template slot="image-actions" slot-scope="{ image: photo }">
        <md-button
          v-if="
            !photo.original.isRejected &&
            canChangePhotoreason &&
            (isES || isCP) &&
            concepts.length > 0
          "
          @click.stop="
            markedReject[photo.original.id] = !markedReject[photo.original.id]
          "
          class="md-just-icon md-round reject"
          :class="{
            'md-danger': markedReject[photo.original.id],
            'md-secondary': !markedReject[photo.original.id],
          }"
        >
          <md-icon>backspace</md-icon>
          <md-tooltip class="md-primary">Presione el boton para rechazar la foto</md-tooltip>
        </md-button>
        <md-button
          @click.stop="changeVisible(photo)"
          class="md-just-icon md-round visible"
          :disabled="!canChangePhotoreason || dontAllowClientVisibilityChange || loading"
          :style="!photo.original.clientVisibility ? 'opacity: 0.85;' : ''"
          :class="{
            'md-plain': !photo.original.clientVisibility,
            'md-primary': photo.original.clientVisibility,
          }"
        >
          <md-icon>people_alt</md-icon>
          <md-tooltip class="md-primary" :disabled="false">
            <template>{{ photo.original.clientVisibility ? 'El archivo es visible por el cliente' : 'Presione el boton para hacer el archivo visible para el cliente' }}</template>
          </md-tooltip>
        </md-button>
        <md-button
          v-if="photo.original.isRejected || isES"
          class="md-just-icon md-round edit md-primary"
          :disabled="!canChangePhotoreason"
          @click="toEditItem(photo.original.id)"
        >
          <md-icon>edit</md-icon>
          <md-tooltip class="md-primary" :disabled="false">Cambiar foto</md-tooltip>
        </md-button>
        <md-button
          v-if="isES"
          class="md-just-icon md-round edit md-danger"
          @click="$emit('deleteItem', photo.original.id)"
        >
          <md-icon>delete</md-icon>
          <md-tooltip class="md-primary" :disabled="false">Eliminar foto</md-tooltip>
        </md-button>
      </template>

      <md-speed-dial class="md-bottom-right float-right" md-direction="top">
        <md-speed-dial-target
          class="md-info md-round md-just-icon big-button"
          @click="toRejectPhotos"
          v-if="!!rejectPhotos.length"
        >
          <md-icon>cancel_presentation</md-icon>
          <md-tooltip>
            <span v-show="!!rejectPhotos.length">
              Se recharazá{{ rejectPhotos.length === 1 ? "" : "n" }}
              {{ rejectPhotos.length }} foto{{
              rejectPhotos.length === 1 ? "" : "s"
              }}.
            </span>
          </md-tooltip>
        </md-speed-dial-target>

        <md-speed-dial-content v-if="!!rejectPhotos.length">
          <md-button class="md-fab md-danger md-round md-just-icon" @click="resetMarked">
            <md-icon>close</md-icon>
            <md-tooltip>Quitar selecciones</md-tooltip>
          </md-button>
        </md-speed-dial-content>
      </md-speed-dial>
    </photo-list>

    <slot name="empty-state" v-else-if="!notDisplayEmptyContent">
      <loading-content v-bind="loadingContentInfo" />
    </slot>
  </div>
</template>

<script>
import { PhotoList } from "@/components";
import usesAccountInfoMixin from "@/mixins/usesAccountInfo";

import { mapPermissions } from "@/plugins/accountPermissions/";

const DEFAULT_EMPTY_STATE = {
  emptyLabel: "No hay fotos",
  emptyDescription: "Las fotos subidas aparecerán aquí.",
  emptyIcon: "photo",
};

export default {
  name: "PhotoEvidencesComponent",

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    concepts: {
      type: Array,
      required: true,
      default: () => [],
    },
    loadingContentInfo: {
      type: Object,
      default: () => DEFAULT_EMPTY_STATE,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isES: {
      type: Boolean,
      default: false,
    },
    isCP: {
      type: Boolean,
      default: false,
    },
    noImageActions: {
      type: Boolean,
      default: false,
    },
    notDisplayEmptyContent: {
      type: Boolean,
      default: false,
    },
    showLabels: {
      type: Boolean,
      default: false,
    },
    useFileIcons: {
      props: Boolean,
      default: false,
    },
    isExtranet: {
      default: false,
      type: Boolean,
    },
    dontAllowClientVisibilityChange: Boolean,
  },

  components: {
    PhotoList,
  },

  mixins: [usesAccountInfoMixin],

  computed: {
    ...mapPermissions("WorkPlan", "photoreason"),
    conceptsById() {
      const obj = {};

      for (const concept of this.concepts) {
        obj[concept.id] = concept;
      }

      return obj;
    },
    photosByConcept() {
      const photos = this.value;

      const obj = {};

      /* if (!Object.keys(this.conceptsById).length) { */
      /* return obj */
      /* } */

      for (const photo of photos) {
        const concept = this.conceptsById[photo.conceptId];
        const cName = concept ? concept.name : "";

        const conceptName = concept ? `${photo.conceptId} - ${cName}` : cName;

        const stuff = obj[conceptName];
        const photoObj = {
          src: photo.file,
          thumb: photo.smallPhoto,
          original: photo,
          label: photo.label,
          // Customers don't need it; sizes matter for the expediente email
          size: this.isExtranet ? undefined : photo.size,
          conceptName,
        };
        if (!stuff) {
          obj[conceptName] = [photoObj];
        } else {
          stuff.push(photoObj);
        }
      }

      return obj;
    },
    missingConcepts() {
      const photosByConcept = this.photosByConcept;
      return this.concepts
        .filter(
          (concept) => !photosByConcept[`${concept.id} - ${concept.name}`]
        )
        .map(({ name, id }) => `${id} - ${name}`);
    },
    rejectPhotos() {
      return Object.entries(this.markedReject)
        .filter(([p, status]) => status)
        .map(([photo]) => parseInt(photo, 10));
    },
  },

  data() {
    return {
      markedReject: {},
      toRejectImages: [],
    };
  },

  watch: {
    value: {
      immediate: true,
      handler: "resetMarked",
    },
    missingConcepts: {
      immediate: true,
      handler(value = []) {
        this.$emit("hasAllConceptPhotos", value.length === 0);
      },
    },
  },

  methods: {
    resetMarked() {
      const { value: photos } = this;
      const rejected = {};
      photos.forEach((photo) => {
        rejected[photo.id] = false;
      });

      this.markedReject = { ...rejected };
    },
    changeVisible({ original: { fileId, clientVisibility } }) {
      this.$emit("changeVisible", { id: fileId, status: !clientVisibility });
    },
    toRejectPhotos() {
      this.$emit("toRejectPhotos", this.rejectPhotos);
    },
    toEditItem(photo) {
      this.$emit("toEditItem", photo);
    },
  },
};
</script>

<style lang="scss" scoped>
.helper-text {
  position: absolute;
}
.reject-button {
  margin-right: 20px !important;
}

.visible {
  top: 0px;
  right: 2px;
}
.reject {
  top: 32px;
  right: 2px;
}
.edit {
  top: 32px;
  right: 2px;
}
.md-tooltip {
  z-index: 110;
}
.md-speed-dial {
  margin-bottom: -10px;
  margin-right: -40px;
}
</style>



// WEBPACK FOOTER //
// src/pages/WorkPlan/PhotoEvidencesList.vue